import React, { useState, useCallback } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  Chip,
  Autocomplete,
  FormControlLabel,
  Switch,
  Divider,
  AppBar,
  Toolbar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Avatar,
  Grid,
} from '@mui/material';

import {
  ArrowBack,
  Business,
  CheckCircle,
  Search,
  CreditCard,
  HourglassTop,
  Info,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { collection, addDoc, doc, updateDoc, serverTimestamp, query, where, getDocs, orderBy, limit, Timestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';
import {
  VenueAddress,
  BusinessHours,
  ExistingVenue,
  VenueFormData,
  VenueApprovalRequest,
  BUSINESS_TYPES,
  STATES,
  PRICE_RANGES,
  CUISINE_OPTIONS,
  AMENITY_OPTIONS,
  DAYS_OF_WEEK,
  DAY_LABELS,
} from '../../types/CreateVenueTypes';
import { alertStyles } from '../../theme/glassTheme';
import ImprovedVenueSearch from './ImprovedVenueSearch';


const CreateVenue: React.FC = () => {
  const navigate = useNavigate();
  const { venueAdminData } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false); // NEW: Track submission state
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Workflow state
  const [workflowMode, setWorkflowMode] = useState<'search' | 'claim' | 'create' | null>(null);
  const [selectedVenue, setSelectedVenue] = useState<any | null>(null);
  const [justification, setJustification] = useState('');

  // Initialize form data with default business hours
  const getDefaultBusinessHours = (): BusinessHours => {
    const defaultHours: BusinessHours = {};
    DAYS_OF_WEEK.forEach(day => {
      defaultHours[day] = {
        open: '09:00',
        close: '17:00',
        closed: day === 'sunday' // Default Sunday closed
      };
    });
    return defaultHours;
  };

  const [formData, setFormData] = useState<VenueFormData>({
    businessName: '',
    tradingName: '',
    email: '',
    phone: '',
    abn: '',
    businessType: '',
    address: {
      street: '',
      suburb: '',
      city: '',
      state: '',
      postcode: '',
      country: 'Australia'
    },
    description: '',
    website: '',
    priceRange: '',
    cuisine: [],
    amenities: [],
    businessHours: getDefaultBusinessHours(),
    maxCapacity: '',
    termsAccepted: false,
    privacyPolicyAccepted: false,
    marketingConsent: false
  });

  const steps = [
    'Search & Verify Venue',
    'Basic Information',
    'Address & Contact',
    'Business Profile',
    'Business Hours',
    'Review & Submit'
  ];

  /**
   * Handle venue claimed from ImprovedVenueSearch
   */
  const handleVenueLinked = async (venueId: string, venueData: any) => {
    console.log('✅ Venue linked successfully:', venueId);
    
    // Pre-fill form data from the claimed venue
    const addressParts = venueData.address?.split(',') || [];
    
    setFormData({
      ...formData,
      businessName: venueData.businessName || venueData.name,
      tradingName: venueData.name || venueData.tradingName,
      address: {
        street: addressParts[0]?.trim() || '',
        suburb: addressParts[1]?.trim() || '',
        city: addressParts[1]?.trim() || '',
        state: extractState(venueData.address) || '',
        postcode: extractPostcode(venueData.address) || '',
        country: 'Australia'
      }
    });

    setSelectedVenue({ ...venueData, id: venueId });
    setWorkflowMode('claim');
    setSuccess('Venue claimed successfully! Please complete the remaining details.');
    setActiveStep(1); // Move to Basic Information step
  };

  /**
   * Handle "Create New Venue" from ImprovedVenueSearch
   */
  const handleCreateNew = () => {
    console.log('➕ Creating new venue');
    setWorkflowMode('create');
    setActiveStep(1); // Move to Basic Information step
  };

  /**
   * Extract state from address string
   */
  const extractState = (address: string): string => {
    const stateMatch = address?.match(/\b(NSW|VIC|QLD|SA|WA|TAS|NT|ACT)\b/i);
    return stateMatch ? stateMatch[1].toUpperCase() : '';
  };

  /**
   * Extract postcode from address string
   */
  const extractPostcode = (address: string): string => {
    const postcodeMatch = address?.match(/\b(\d{4})\b/);
    return postcodeMatch ? postcodeMatch[1] : '';
  };

  /**
   * Submit venue claim request
   */
  const submitClaimRequest = async (): Promise<void> => {
    if (!venueAdminData?.adminId) {
      throw new Error('No admin ID found');
    }

    if (!selectedVenue?.id) {
      throw new Error('No venue selected');
    }

    const now = serverTimestamp();

    const approvalRequest: VenueApprovalRequest = {
      venueId: selectedVenue.id,
      adminId: venueAdminData.adminId,
      adminName: venueAdminData.name,
      adminEmail: venueAdminData.email,
      requestType: 'claim',
      venueData: formData,
      businessName: formData.businessName,
      tradingName: formData.tradingName,
      address: `${formData.address.street}, ${formData.address.suburb} ${formData.address.state} ${formData.address.postcode}, ${formData.address.country}`,
      businessType: formData.businessType,
      justification,
      status: 'pending',
      submittedAt: now
    };

    // Create approval request document (this is the critical operation)
    await addDoc(collection(db, 'venue_approval_requests'), approvalRequest);

    // Try to mark venue as under review (optional - may fail due to permissions)
    try {
      await updateDoc(doc(db, 'venues', selectedVenue.id), {
        portalStatus: 'under_review',
        updatedAt: now
      });
      console.log('✅ Venue status updated to under_review');
    } catch (updateError: any) {
      // Log the error but don't fail the entire request
      // The approval request was created successfully
      console.warn('⚠️ Could not update venue status (will be updated during approval):', updateError.message);
    }
  };

  /**
   * Submit new venue creation request
   */
  const submitCreateRequest = async (): Promise<void> => {
    if (!venueAdminData?.adminId) {
      throw new Error('No admin ID found');
    }

    if (!formData.businessName || !formData.tradingName || !formData.businessType) {
      throw new Error('Missing required form data');
    }

    if (!formData.address.street || !formData.address.suburb || !formData.address.city) {
      throw new Error('Missing required address information');
    }

    const now = serverTimestamp();
    const fullAddress = `${formData.address.street}, ${formData.address.suburb} ${formData.address.state} ${formData.address.postcode}, ${formData.address.country}`;

    const approvalRequest: VenueApprovalRequest = {
      adminId: venueAdminData.adminId,
      adminName: venueAdminData.name,
      adminEmail: venueAdminData.email,
      requestType: 'create',
      venueData: formData,
      businessName: formData.businessName,
      tradingName: formData.tradingName,
      address: fullAddress,
      businessType: formData.businessType,
      status: 'pending',
      submittedAt: now
    };

    // Create approval request document
    await addDoc(collection(db, 'venue_approval_requests'), approvalRequest);
  };

  const handleInputChange = (field: keyof VenueFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddressChange = (field: keyof VenueAddress, value: string) => {
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value
      }
    }));
  };

  const handleBusinessHoursChange = (day: string, field: 'open' | 'close' | 'closed', value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      businessHours: {
        ...prev.businessHours,
        [day]: {
          ...prev.businessHours[day],
          [field]: value
        }
      }
    }));
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 0: // Search & Verify
        return workflowMode !== null;
      case 1: // Basic Information
        return !!(formData.businessName && formData.tradingName && formData.businessType);
      case 2: // Address & Contact
        return !!(formData.email && formData.address.street && formData.address.suburb && 
                 formData.address.city && formData.address.postcode);
      case 3: // Business Profile
        return true; // Optional fields
      case 4: // Business Hours
        return true; // Default hours are provided
      case 5: // Review
        return formData.termsAccepted && formData.privacyPolicyAccepted && 
               (workflowMode === 'claim' ? justification.trim().length > 0 : true);
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
      setError('');
    } else {
      setError('Please fill in all required fields before continuing.');
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
    setError('');
  };

  const handleSubmit = async () => {
    // Prevent duplicate submissions
    if (submitting) {
      console.log('⚠️ Submission already in progress');
      return;
    }

    if (!validateStep(5)) {
      setError('Please complete all required fields and accept the terms and conditions.');
      return;
    }

    if (!venueAdminData?.adminId) {
      setError('Authentication error. Please log in again.');
      return;
    }

    // Check venue limit (only for creating new venues, not claims)
    if (workflowMode === 'create' && venueAdminData.venueCount >= venueAdminData.venueLimit) {
      setError(`You have reached your venue limit (${venueAdminData.venueLimit} venues). Please upgrade your plan to add more venues.`);
      return;
    }

    try {
      setSubmitting(true);
      setLoading(true);
      setError('');

      if (workflowMode === 'claim') {
        await submitClaimRequest();
        setSuccess('✅ Venue claim request submitted successfully! We will review your request and notify you once approved.');
      } else {
        await submitCreateRequest();
        setSuccess('✅ Venue creation request submitted successfully! We will review your request and notify you once approved.');
      }

      // Navigate back to dashboard after delay
      setTimeout(() => {
        navigate('/venue-admin');
      }, 3000);

    } catch (err: any) {
      console.error('❌ Error submitting request:', err);
      setError(`Failed to submit request: ${err.message}`);
      setSubmitting(false); // Re-enable button on error
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="600">
                Search for Your Venue
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                First, let's check if your venue already exists in our system. If it does, you can claim it. 
                Otherwise, you can create a new venue listing.
              </Typography>

              {!workflowMode ? (
                <ImprovedVenueSearch
                  currentAdminId={venueAdminData?.adminId || ''}
                  onVenueLinked={handleVenueLinked}
                  onCreateNew={handleCreateNew}
                />
              ) : (
                <Box>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {workflowMode === 'claim' 
                      ? '✓ Venue claimed! Continue to complete your venue details.'
                      : '✓ Creating new venue! Fill in the details below.'}
                  </Alert>
                  <Button
                    startIcon={<ArrowBack />}
                    onClick={() => {
                      setWorkflowMode(null);
                      setSelectedVenue(null);
                      setActiveStep(0);
                    }}
                  >
                    Start Over
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        );

      case 1:
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="600">
                Basic Information
              </Typography>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Business Name *"
                    value={formData.businessName}
                    onChange={(e) => handleInputChange('businessName', e.target.value)}
                    helperText="Legal name of your business"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Trading Name *"
                    value={formData.tradingName}
                    onChange={(e) => handleInputChange('tradingName', e.target.value)}
                    helperText="Name customers know you by"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Business Type *</InputLabel>
                    <Select
                      value={formData.businessType}
                      onChange={(e) => handleInputChange('businessType', e.target.value)}
                      label="Business Type *"
                    >
                      {BUSINESS_TYPES.map(type => (
                        <MenuItem key={type} value={type}>{type}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="ABN"
                    value={formData.abn}
                    onChange={(e) => handleInputChange('abn', e.target.value)}
                    helperText="Australian Business Number (optional)"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        );

      case 2:
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="600">
                Address & Contact Details
              </Typography>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Street Address *"
                    value={formData.address.street}
                    onChange={(e) => handleAddressChange('street', e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Suburb *"
                    value={formData.address.suburb}
                    onChange={(e) => handleAddressChange('suburb', e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="City *"
                    value={formData.address.city}
                    onChange={(e) => handleAddressChange('city', e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <FormControl fullWidth>
                    <InputLabel>State *</InputLabel>
                    <Select
                      value={formData.address.state}
                      onChange={(e) => handleAddressChange('state', e.target.value)}
                      label="State *"
                    >
                      {STATES.map(state => (
                        <MenuItem key={state} value={state}>{state}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Postcode *"
                    value={formData.address.postcode}
                    onChange={(e) => handleAddressChange('postcode', e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Country"
                    value={formData.address.country}
                    onChange={(e) => handleAddressChange('country', e.target.value)}
                    disabled
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Email *"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        );

      case 3:
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="600">
                Business Profile
              </Typography>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Description"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    helperText="Tell customers about your venue"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Website"
                    value={formData.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Price Range</InputLabel>
                    <Select
                      value={formData.priceRange}
                      onChange={(e) => handleInputChange('priceRange', e.target.value)}
                      label="Price Range"
                    >
                      {PRICE_RANGES.map((range) => (
                        <MenuItem key={range} value={range}>
                          {range} {range === '$' && '(Under $20)'} 
                          {range === '$$' && '($20-40)'} 
                          {range === '$$$' && '($40-80)'} 
                          {range === '$$$$' && '($80+)'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Autocomplete
                    multiple
                    options={CUISINE_OPTIONS}
                    value={formData.cuisine}
                    onChange={(_, newValue) => handleInputChange('cuisine', newValue)}
                    renderInput={(params) => (
                      <TextField {...params} label="Cuisine Types" />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Autocomplete
                    multiple
                    options={AMENITY_OPTIONS}
                    value={formData.amenities}
                    onChange={(_, newValue) => handleInputChange('amenities', newValue)}
                    renderInput={(params) => (
                      <TextField {...params} label="Amenities" />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Max Capacity"
                    type="number"
                    value={formData.maxCapacity}
                    onChange={(e) => handleInputChange('maxCapacity', e.target.value)}
                    inputProps={{ min: 0 }}
                    helperText="This will assist in venue capacity accuracy"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        );

      case 4:
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="600">
                Business Hours
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Set your opening hours for each day of the week
              </Typography>
              <Grid container spacing={2}>
                {DAYS_OF_WEEK.map(day => (
                  <Grid size={{ xs: 12 }} key={day}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid size={{ xs: 12, sm: 3 }}>
                          <Typography fontWeight="500">
                            {DAY_LABELS[day as keyof typeof DAY_LABELS]}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 3 }}>
                          <TextField
                            fullWidth
                            type="time"
                            label="Open"
                            value={formData.businessHours[day].open}
                            onChange={(e) => handleBusinessHoursChange(day, 'open', e.target.value)}
                            disabled={formData.businessHours[day].closed}
                            size="small"
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 3 }}>
                          <TextField
                            fullWidth
                            type="time"
                            label="Close"
                            value={formData.businessHours[day].close}
                            onChange={(e) => handleBusinessHoursChange(day, 'close', e.target.value)}
                            disabled={formData.businessHours[day].closed}
                            size="small"
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 3 }}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={!formData.businessHours[day].closed}
                                onChange={(e) => handleBusinessHoursChange(day, 'closed', !e.target.checked)}
                              />
                            }
                            label="Open"
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        );

      case 5:
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="600">
                Review & Submit
              </Typography>

              {workflowMode === 'claim' && (
                <Box mb={3}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight="600" gutterBottom>
                      You are claiming: {selectedVenue?.name}
                    </Typography>
                    <Typography variant="body2">
                      Address: {selectedVenue?.address}
                    </Typography>
                  </Alert>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Justification for Claim *"
                    value={justification}
                    onChange={(e) => setJustification(e.target.value)}
                    helperText="Please explain why you should be granted ownership of this venue"
                    required
                  />
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                Business Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="text.secondary">Business Name:</Typography>
                  <Typography variant="body2" fontWeight="500">{formData.businessName}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="text.secondary">Trading Name:</Typography>
                  <Typography variant="body2" fontWeight="500">{formData.tradingName}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="text.secondary">Business Type:</Typography>
                  <Typography variant="body2" fontWeight="500">{formData.businessType}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="text.secondary">ABN:</Typography>
                  <Typography variant="body2" fontWeight="500">{formData.abn || 'Not provided'}</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.termsAccepted}
                      onChange={(e) => handleInputChange('termsAccepted', e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I accept the Terms and Conditions *
                    </Typography>
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.privacyPolicyAccepted}
                      onChange={(e) => handleInputChange('privacyPolicyAccepted', e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I accept the Privacy Policy *
                    </Typography>
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.marketingConsent}
                      onChange={(e) => handleInputChange('marketingConsent', e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I agree to receive marketing communications
                    </Typography>
                  }
                />
              </Box>

              {workflowMode === 'claim' && (
                <Alert severity="warning" sx={{ mt: 3 }}>
                  <Typography variant="body2">
                    <strong>Important:</strong> Your claim will be reviewed by our team. 
                    You will be notified once the review is complete.
                  </Typography>
                </Alert>
              )}
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/venue-admin')}
          sx={{ mb: 2 }}
        >
          Back to Dashboard
        </Button>
        <Typography variant="h4" fontWeight="600" gutterBottom>
          {workflowMode === 'claim' ? 'Claim Venue' : 'Add New Venue'}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {workflowMode === 'claim' 
            ? 'Complete the details to claim this existing venue'
            : 'Search for your venue or create a new listing'}
        </Typography>
      </Box>

      {/* Stepper */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Step Content */}
      <Box mb={3}>
        {renderStepContent()}
      </Box>

      {/* Navigation Buttons */}
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between">
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
          >
            Back
          </Button>
          <Box>
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={submitting || loading || !validateStep(activeStep)}
                startIcon={loading ? <CircularProgress size={20} /> : submitting ? <HourglassTop /> : <CheckCircle />}
              >
                {submitting ? 'Submitting...' : loading ? 'Processing...' : 'Submit Request'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!validateStep(activeStep)}
              >
                Next
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default CreateVenue;